import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { ISystemSettings } from "@/Settings/types/Common/model";
import { IOem } from "@/Settings/types/SystemSettings/appendingConfiguration";

import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";

const systemSettingsQuery = gql`
	query getSystemSettings($locationId: String!, $oems: [OemInput]) {
		getSystemSettings(locationId: $locationId, oems: $oems) {
			_id
			technicalContacts
			locationId
			isGlobalRanking
			maxAllowableBorrowedPTO
			citMaxGraphValues {
				maxUnfundedDollars
				maxUnfundedContracts
			}
			citGraphWidth
			citCommissionPayout {
				isPayFundedDealsOnly
				toPositions
			}
			citEmailDistribution {
				toIndividuals {
					_id
					employee {
						_id
						fullName
						employeeId
					}
				}
				toNonEmployee
				accountNumbers {
					sourceAccount
					destinationAccount
					sourceLabelId
				}
			}
			crossStoreEmailDistribution {
				toIndividuals {
					_id
					employeeId
				}
				toNonEmployee
				accountMappings {
					sourceGlAccount
					destinationStore {
						storeId
						storeName
					}
					destinationGlAccount
					destinationSourceLabel {
						label
						value
					}
					destinationControlType {
						label
						value
						identifier
					}
				}
			}
			citMinimumBalance
			citMaxScheduleDays
			citScheduledAccountNumbers {
				label
				values
			}
			pvrMaxGraphValues {
				maxNewVehiclesPVR
				maxPreOwnedVehiclesPVR
				maxFinancePVR
				maxTotalPVR
			}
			mappedPositions {
				systemPosition
				isDepartmental
				overtime
			}
			mappedPayItems {
				_id
				systemPayItem
				type
				hoursMemo
				effectOnPay
			}
			chargebackLimits {
				lastModifiedOn
				position
				value
				lastModifiedBy {
					_id
					fullName
				}
			}
			chargebackTypes {
				position
				grossChargebackTypes {
					chargebackCode
					productType
				}
				deductionsChargebackTypes {
					chargebackCode
					earningPayItem
					deductionPayItem
				}
			}
			chargebackAccountReferences {
				position
				chargebackTypes {
					label
					description
					repairOrderRoute
					accountNumbers
				}
			}
			advisorsRankingConfiguration {
				isRankingRO
				category
			}
			defaultPayPlans {
				position
				defaultPayPlan
				newVehiclePayPlan
				preOwnedVehiclePayPlan
			}
			drawModalities {
				position
				drawModality
			}
			workingDays {
				position
				workingDays
			}
			commissionCyclesPerMonth {
				lastModifiedOn
				value
				lastModifiedBy {
					_id
					fullName
				}
			}
			washoutReviewers {
				lastModifiedOn
				position
				isDirectSupervisor
				reviewerId {
					_id
					fullName
				}
				lastModifiedBy {
					_id
					fullName
				}
			}
			commissionSupportTeam {
				_id
				fullName
				position
				employeeId
				communications {
					communicationType
					usage
					value
				}
			}
			callActivityPerformance {
				maxAllowableTurnaroundTime {
					hours
					minutes
				}
				minimumRingDuration {
					seconds
				}
				minimumTalkDuration {
					minutes
					seconds
				}
				minimumTotalDuration {
					minutes
					seconds
				}
				enableTalkTime
				enableRingTime
				enableTotalTime
			}
			salesLogs {
				franchises
				exportFormat
				dailyDistributions
				toPositions
				toIndividuals {
					_id
					employee {
						communications {
							value
							usage
							communicationType
							acceptsTxt
						}
						employeeId
						fullName
						_id
					}
				}
				toEmails
			}
			salesLogInitialDataFilter {
				name
				filterOptions {
					name
					included
					enabled
					disabled
				}
			}
			minimumWage
			blacklistPhoneNumbers {
				_id
				phoneNumber
				createdAt
			}
			appendingConfiguration {
				_id
				position
				data {
					_id
					reference
					oems {
						_id
						configuration {
							_id
							franchise
							isCombined
							groups {
								franchiseName
							}
							data {
								_id
								accountDescription
								fsAccount
								glAccount {
									glAccount
									accountType
								}
								groupInfo {
									group
									subGroup
								}
								arithmeticExpression {
									operation
									value
								}
								properties {
									isChargeback
								}
							}
						}
						oemReference
						dealerCodeFS
						fsVersion
						version
					}
				}
			}
			payplanTermConditions {
				position
				inEditionBy {
					_id
					fullName
				}
				termConditions {
					_id
					componentName
					sectionTitle
					content
					createdAt
					createdBy
					updatedAt
					updatedBy
				}
			}
		}
	}
`;

export async function getSystemSettings(locationId: string, oems?: IOem[]) {
	const variables = {
		locationId,
		oems,
	};

	try {
		const response: GraphqlQueryResponse<"getSystemSettings", ISystemSettings> = await apiInstance.post("/graphql", {
			query: systemSettingsQuery,
			variables,
		});
		return response.data.data?.getSystemSettings;
	} catch (err) {
		errorLogger(err as Error, { function: "getSystemSettings", message: "Error retrieving system settings", variables });
		return null;
	}
}

export const getSystemSettingsAdditionalInfo = async ({ locationId }: { locationId: string }) => {
	const query = gql`
		query getSystemSettingsAdditionalInfo($locationId: String!) {
			getEmployees(locationId: $locationId) {
				_id
				employeeId
				fullName
				jobTitle
			}
			getAdministrationTools(locationId: $locationId) {
				_id
				integrations {
					dmsSources {
						_id
						label
						value
					}
				}
			}
			getFlags(locationId: $locationId) {
				_id
				name
				icon
				abbr
				code
				mask
			}
			getUsersSystemSettings(locationId: $locationId) {
				_id
				username
				locationId
				position
				payrollPosition
				fullName
				employeeId
				supervisor {
					_id
					fullName
				}
				communications {
					acceptsTxt
					communicationType
					usage
					value
				}
				preferences {
					landingLocation
					landingPage
				}
				currentStatus {
					status
				}
			}
			getAccountingMembers(locationId: $locationId) {
				_id
				label
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data;
	} catch (error) {
		errorLogger(error as Error, {
			function: "getSystemSettingsAdditionalInfo",
			message: "Error retrieving system settings additional info",
			variables,
		});
		return null;
	}
};

export const getDepartmentHierarchy = async (locationId: string, departments: string[]) => {
	const query = gql`
		query getDepartmentHierarchy($departments: [String], $locationId: String!) {
			getDepartmentHierarchy(departments: $departments, locationId: $locationId) {
				_id
				department
				path
			}
		}
	`;
	const variables = {
		locationId,
		departments,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data.getDepartmentHierarchy;
	} catch (err) {
		errorLogger(err as Error, { function: "getAppendingConfigInfo", message: "Error retrieving getAppendingConfigInfo", variables });
		return null;
	}
};

export const getAppendingConfigInfo = async ({ locationId, oems }: { locationId: string; oems: IOem[] }) => {
	const query = gql`
		query getAppendingConfigInfo($locationId: String, $oems: [OemInput]) {
			getFSAccounts(locationId: $locationId, oems: $oems) {
				oemReference
				dealerCodeFS
				processes {
					process
					accounts
				}
			}
			getGlAccounts(locationId: $locationId, oems: $oems) {
				oemReference
				dealerCodeFS
				glAccount {
					glAccount
					accountType
				}
				accountDescription
				fsAccount
			}
			getWSGroups(locationId: $locationId, oems: $oems) {
				oemReference
				version
				fsVersion
				section
				franchises {
					franchise
					groups {
						group
						subgroups
					}
				}
				groups {
					group
					subgroups
				}
			}
		}
	`;

	const variables = {
		locationId,
		oems,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data;
	} catch (err) {
		errorLogger(err as Error, { function: "getAppendingConfigInfo", message: "Error retrieving getAppendingConfigInfo", variables });
		return null;
	}
};

export async function getChargebackDeductionCodes(positions: string[]) {
	const query = gql`
		query getChargebackDeductionCodes($positions: [String]) {
			getChargebackDeductionCodes(positions: $positions) {
				_id
				position
				codes {
					code
					description
					isDepartmentalOnly
				}
			}
		}
	`;

	const variables = {
		positions,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getChargebackDeductionCodes;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getChargebackDeductionCodes",
			message: "Error retrieving Chargeback Deduction Codes",
			variables,
		});
		return null;
	}
}

export async function getDepartmentChargebackCodes(department?: string) {
	const query = gql`
		query getDepartmentChargebackCodes($department: String) {
			getDepartmentChargebackCodes(department: $department) {
				_id
				code
				description
				department
			}
		}
	`;

	const variables = {
		department,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getDepartmentChargebackCodes;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getDepartmentChargebackCodes",
			message: "Error retrieving Department Chargeback Codes",
			variables,
		});
		return null;
	}
}

export interface IGetSystemSettingsScheduledAccountNumbers {
	_id: string;
	citMinimumBalance: number;
	citMaxScheduleDays: number;
	citScheduledAccountNumbers: {
		label: string;
		values: string[];
	}[];
	citGraphWidth: string;
}

export async function getSystemSettingsScheduledAccountNumbers(locationId: string) {
	const query = gql`
		query getSystemSettings($locationId: String!) {
			getSystemSettings(locationId: $locationId) {
				_id
				citMinimumBalance
				citMaxScheduleDays
				citScheduledAccountNumbers {
					label
					values
				}
				citGraphWidth
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getSystemSettings", IGetSystemSettingsScheduledAccountNumbers> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data.data?.getSystemSettings;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getDrawModalities(locationId: string) {
	interface GetDrawModalitiesResponse extends Pick<ISystemSettings, "drawModalities"> {}
	const query = gql`
		query getDrawModalities($locationId: String!) {
			getSystemSettings(locationId: $locationId) {
				drawModalities {
					position
					drawModality
				}
			}
		}
	`;

	const variables = { locationId };

	const response: GraphqlQueryResponse<"getSystemSettings", GetDrawModalitiesResponse> = await apiInstance.post("/graphql", {
		query,
		variables,
	});
	return response.data.data?.getSystemSettings?.drawModalities ?? [];
}
